import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CostCentersService } from '../../../services/cost-centers.service';
import { FinancialReportsService } from '../../../services/financial-reports.service';
import { CostCenter } from '../../../models/CostCenter';
import { ErrorService } from '../../../shared/services/error.service';
import {
  ApPositionSummaryResponse,
  FinancialReportPeriodPreset,
  FINANCIAL_REPORT_PERIOD_OPTIONS
} from '../../../models/financial-report.models';
import {
  exportApPositionSummaryToExcel,
  exportApPositionSummaryToPdf
} from '../../../utils/financial-report-export.util';

@Component({
  selector: 'app-ap-position-summary',
  templateUrl: './ap-position-summary.component.html',
  styleUrls: ['./ap-position-summary.component.css']
})
export class ApPositionSummaryComponent implements OnInit {
  readonly periodOptions = FINANCIAL_REPORT_PERIOD_OPTIONS;

  report: ApPositionSummaryResponse | null = null;
  loading = false;
  error: string | null = null;
  isExporting = false;

  showParamsModal = false;

  /** Parâmetros aplicados na última consulta. */
  appliedPeriod: FinancialReportPeriodPreset = 'last30';
  appliedCostCenterId: number | null = null;
  appliedIncludeChildCostCenters = true;

  /** Rascunho no modal (antes de gerar). */
  draftPeriod: FinancialReportPeriodPreset = 'last30';
  draftCostCenterId: number | null = null;
  draftIncludeChildCostCenters = true;

  costCenters: CostCenter[] = [];

  constructor(
    private financialReportsService: FinancialReportsService,
    private costCentersService: CostCentersService,
    private errorService: ErrorService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadCostCenters();
    this.loadReport();
  }

  loadCostCenters(): void {
    this.costCentersService.getActive().subscribe({
      next: (result) => {
        this.costCenters = result.items || [];
      },
      error: () => {
        this.costCenters = [];
      }
    });
  }

  openParamsModal(): void {
    this.draftPeriod = this.appliedPeriod;
    this.draftCostCenterId = this.appliedCostCenterId;
    this.draftIncludeChildCostCenters = this.appliedIncludeChildCostCenters;
    this.showParamsModal = true;
  }

  closeParamsModal(): void {
    this.showParamsModal = false;
  }

  applyParamsAndLoad(): void {
    this.appliedPeriod = this.draftPeriod;
    this.appliedCostCenterId = this.draftCostCenterId;
    this.appliedIncludeChildCostCenters = this.draftIncludeChildCostCenters;
    this.showParamsModal = false;
    this.loadReport();
  }

  loadReport(): void {
    this.loading = true;
    this.error = null;

    this.financialReportsService
      .getApPositionSummary({
        period: this.appliedPeriod,
        costCenterId: this.appliedCostCenterId,
        includeChildCostCenters: this.appliedIncludeChildCostCenters
      })
      .subscribe({
        next: (data) => {
          this.report = data;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.report = null;
          this.error = err?.message || 'Não foi possível carregar o relatório.';
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
  }

  exportExcel(): void {
    if (!this.report?.summary || this.isExporting) return;
    this.isExporting = true;
    try {
      exportApPositionSummaryToExcel(this.report.summary, this.report.generatedAtUtc);
      this.errorService.showInfoMessage('Planilha Excel exportada com sucesso.');
    } catch {
      this.errorService.showErrorMessage('Erro ao gerar planilha Excel.');
    } finally {
      this.isExporting = false;
      this.cdr.markForCheck();
    }
  }

  exportPdf(): void {
    if (!this.report?.summary || this.isExporting) return;
    this.isExporting = true;
    try {
      exportApPositionSummaryToPdf(this.report.summary, this.report.generatedAtUtc);
      this.errorService.showInfoMessage('PDF exportado com sucesso.');
    } catch {
      this.errorService.showErrorMessage('Erro ao gerar PDF.');
    } finally {
      this.isExporting = false;
      this.cdr.markForCheck();
    }
  }

  formatCurrency(value: number | undefined): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);
  }

  formatDate(iso: string | undefined): string {
    if (!iso) return '—';
    const [y, m, d] = iso.split('T')[0].split('-');
    if (!y || !m || !d) return iso;
    return `${d}/${m}/${y}`;
  }

  get appliedPeriodLabel(): string {
    return this.periodOptions.find((o) => o.value === this.appliedPeriod)?.label ?? this.appliedPeriod;
  }

  get appliedCostCenterLabel(): string {
    if (this.appliedCostCenterId == null) return 'Todos';
    const cc = this.costCenters.find((c) => c.id === this.appliedCostCenterId);
    return cc?.name ?? String(this.appliedCostCenterId);
  }
}
